import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast, Toaster } from 'sonner';
import { driverApplicationsApi } from '../lib/api';
import { 
  User, MapPin, CreditCard, Truck, AlertTriangle, 
  Briefcase, GraduationCap, ShieldCheck, 
  ChevronRight, ChevronLeft, Check, Plus, Trash2, FileText,
  Shield, Scale
} from 'lucide-react';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const EQUIPMENT_TYPES = [
  'Straight Truck', 'Tractor-Trailer', 'Tanker', 'Flatbed', 'Refrigerated',
  'Double/Triple', 'Hazmat', 'Dry Van', 'Car Hauler', 'Oversize Loads', 'Other'
];

const STEPS = [
  { id: 1, label: 'Personal Info', icon: User },
  { id: 2, label: 'Address', icon: MapPin },
  { id: 3, label: 'License', icon: CreditCard },
  { id: 4, label: 'Experience', icon: Truck },
  { id: 5, label: 'Accidents', icon: AlertTriangle },
  { id: 6, label: 'Employment', icon: Briefcase },
  { id: 7, label: 'Education', icon: GraduationCap },
  { id: 8, label: 'MVR Auth', icon: ShieldCheck },
  { id: 9, label: 'FMCSA', icon: Shield },
  { id: 10, label: 'Fair Credit', icon: Scale },
];

const emptyAddress = { street: '', city: '', state: '', zip_code: '', from_date: '', to_date: '' };
const emptyAccident = { date: '', nature: '', fatalities: false, injuries: false, description: '' };
const emptyViolation = { date: '', location: '', charge: '', penalty: '' };
const emptyEmployment = { company_name: '', address: '', city: '', state: '', zip_code: '', phone: '', position: '', from_date: '', to_date: '', reason_for_leaving: '', dot_regulated: false, safety_sensitive: false, supervisor_name: '' };
const emptyReference = { name: '', phone: '', relationship: '', years_known: '' };

export default function DriverApplication() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Apply saved theme to the /apply page (since it's outside Layout)
  useEffect(() => {
    const theme = localStorage.getItem('theme');
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const [form, setForm] = useState({
    first_name: '', last_name: '', middle_name: '', date_of_birth: '', ssn: '', email: '', phone: '',
    current_address: { ...emptyAddress },
    previous_addresses: [],
    license_number: '', license_state: '', license_class: '', license_expiration: '',
    endorsements: '', restrictions: '', medical_card_expiration: '',
    has_license_been_denied: false, denial_details: '',
    has_license_been_suspended: false, suspension_details: '',
    years_experience: '', equipment_experience: [],
    accidents: [],
    violations: [],
    employment_history: [{ ...emptyEmployment }],
    unemployment_periods: '',
    highest_education: '', school_name: '',
    references: [{ ...emptyReference }],
    mvr_signature_name: '', mvr_signature_date: '', mvr_consent: false,
    certify_information_true: false,
    fmcsa_signature_name: '', fmcsa_signature_date: '', fmcsa_consent: false,
    fcra_signature_name: '', fcra_signature_date: '', fcra_consent: false,
  });

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setValidationErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };
  const updateNested = (parent, field, value) => {
    setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
    setValidationErrors(prev => { const n = { ...prev }; delete n[`${parent}.${field}`]; return n; });
  };
  const updateArrayItem = (arrayName, index, field, value) => {
    setForm(prev => {
      const arr = [...prev[arrayName]];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [arrayName]: arr };
    });
  };
  const addArrayItem = (arrayName, template) => setForm(prev => ({ ...prev, [arrayName]: [...prev[arrayName], { ...template }] }));
  const removeArrayItem = (arrayName, index) => setForm(prev => ({ ...prev, [arrayName]: prev[arrayName].filter((_, i) => i !== index) }));

  const toggleEquipment = (type) => {
    setForm(prev => ({
      ...prev,
      equipment_experience: prev.equipment_experience.includes(type)
        ? prev.equipment_experience.filter(t => t !== type)
        : [...prev.equipment_experience, type]
    }));
  };

  const validateStep = (stepNum) => {
    const errors = {};
    switch (stepNum) {
      case 1:
        if (!form.first_name.trim()) errors.first_name = 'First name is required';
        if (!form.last_name.trim()) errors.last_name = 'Last name is required';
        break;
      case 2:
        if (!form.current_address.street.trim()) errors['current_address.street'] = 'Street address is required';
        if (!form.current_address.city.trim()) errors['current_address.city'] = 'City is required';
        if (!form.current_address.state) errors['current_address.state'] = 'State is required';
        if (!form.current_address.zip_code.trim()) errors['current_address.zip_code'] = 'ZIP code is required';
        break;
      case 3:
        if (!form.license_number.trim()) errors.license_number = 'License number is required';
        break;
      case 4:
        if (!form.years_experience.trim()) errors.years_experience = 'Years of experience is required';
        break;
      case 5:
        break;
      case 6:
        if (form.employment_history.length > 0 && !form.employment_history[0].company_name.trim()) {
          errors['employment_0_company'] = 'At least one employer is required';
        }
        break;
      case 7:
        break;
      case 8:
        if (!form.mvr_signature_name.trim()) errors.mvr_signature_name = 'Signature is required';
        if (!form.mvr_signature_date) errors.mvr_signature_date = 'Date is required';
        if (!form.mvr_consent) errors.mvr_consent = 'MVR consent is required';
        if (!form.certify_information_true) errors.certify_information_true = 'Certification is required';
        break;
      case 9:
        if (!form.fmcsa_signature_name.trim()) errors.fmcsa_signature_name = 'Signature is required';
        if (!form.fmcsa_signature_date) errors.fmcsa_signature_date = 'Date is required';
        if (!form.fmcsa_consent) errors.fmcsa_consent = 'FMCSA consent is required';
        break;
      case 10:
        if (!form.fcra_signature_name.trim()) errors.fcra_signature_name = 'Signature is required';
        if (!form.fcra_signature_date) errors.fcra_signature_date = 'Date is required';
        if (!form.fcra_consent) errors.fcra_consent = 'FCRA authorization is required';
        break;
      default:
        break;
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (!validateStep(step)) {
      toast.error('Please fill in all required fields before continuing');
      return;
    }
    setStep(Math.min(STEPS.length, step + 1));
  };

  const goToStep = (targetStep) => {
    if (targetStep > step) {
      if (!validateStep(step)) {
        toast.error('Please complete the current step first');
        return;
      }
    }
    setStep(targetStep);
  };

  const fieldError = (field) => validationErrors[field] ? 'border-red-400 ring-1 ring-red-300' : '';
  const ErrorMsg = ({ field }) => validationErrors[field] ? <p className="text-xs text-red-500 mt-1">{validationErrors[field]}</p> : null;

  const handleSubmit = async () => {
    if (!validateStep(step)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await driverApplicationsApi.submit(form);
      setApplicationId(response.data.id);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <Card className="max-w-lg w-full text-center" data-testid="submission-success-card">
          <CardContent className="pt-10 pb-10 space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Application Submitted!</h2>
            <p className="text-muted-foreground">
              Thank you, {form.first_name}. Your application has been received and is being reviewed. 
              We will contact you at <strong className="text-foreground">{form.email || form.phone}</strong> with next steps.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-foreground truncate">Driver Application</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Complete all sections below</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-xs flex-shrink-0 ml-2 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700" data-testid="step-badge">Step {step} of {STEPS.length}</Badge>
        </div>
      </div>

      {/* Progress Steps - horizontally scrollable */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => goToStep(s.id)}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                  step === s.id
                    ? 'bg-blue-600 text-white'
                    : s.id < step
                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                    : 'bg-muted text-muted-foreground'
                }`}
                data-testid={`step-${s.id}-btn`}
              >
                <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.id}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form Content - with safe bottom padding for fixed footer */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 pb-24 sm:pb-28">
        {/* Step 1: Personal Info */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><User className="h-5 w-5 text-blue-600" /> Personal Information</CardTitle>
              <CardDescription>Provide your basic personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>First Name *</Label>
                  <Input value={form.first_name} onChange={e => updateForm('first_name', e.target.value)} className={fieldError('first_name')} data-testid="first-name-input" />
                  <ErrorMsg field="first_name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Middle Name</Label>
                  <Input value={form.middle_name} onChange={e => updateForm('middle_name', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Last Name *</Label>
                  <Input value={form.last_name} onChange={e => updateForm('last_name', e.target.value)} className={fieldError('last_name')} data-testid="last-name-input" />
                  <ErrorMsg field="last_name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth} onChange={e => updateForm('date_of_birth', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Social Security Number</Label>
                  <Input value={form.ssn} onChange={e => updateForm('ssn', e.target.value)} placeholder="XXX-XX-XXXX" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Email Address</Label>
                  <Input type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} data-testid="email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone Number</Label>
                  <Input value={form.phone} onChange={e => updateForm('phone', e.target.value)} placeholder="(555) 123-4567" data-testid="phone-input" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Address History */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-blue-600" /> Address History</CardTitle>
              <CardDescription>Current address and all addresses for the past 3 years</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-sm mb-3">Current Address *</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label>Street Address *</Label>
                    <Input value={form.current_address.street} onChange={e => updateNested('current_address', 'street', e.target.value)} className={fieldError('current_address.street')} />
                    <ErrorMsg field="current_address.street" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>City *</Label>
                    <Input value={form.current_address.city} onChange={e => updateNested('current_address', 'city', e.target.value)} className={fieldError('current_address.city')} />
                    <ErrorMsg field="current_address.city" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>State *</Label>
                      <Select value={form.current_address.state} onValueChange={v => updateNested('current_address', 'state', v)}>
                        <SelectTrigger className={fieldError('current_address.state')}><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <ErrorMsg field="current_address.state" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>ZIP *</Label>
                      <Input value={form.current_address.zip_code} onChange={e => updateNested('current_address', 'zip_code', e.target.value)} className={fieldError('current_address.zip_code')} />
                      <ErrorMsg field="current_address.zip_code" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>From Date</Label>
                    <Input type="date" value={form.current_address.from_date} onChange={e => updateNested('current_address', 'from_date', e.target.value)} />
                  </div>
                </div>
              </div>

              {form.previous_addresses.map((addr, i) => (
                <div key={i} className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm">Previous Address #{i + 1}</h4>
                    <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeArrayItem('previous_addresses', i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label>Street Address</Label>
                      <Input value={addr.street} onChange={e => updateArrayItem('previous_addresses', i, 'street', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>City</Label>
                      <Input value={addr.city} onChange={e => updateArrayItem('previous_addresses', i, 'city', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>State</Label>
                        <Select value={addr.state} onValueChange={v => updateArrayItem('previous_addresses', i, 'state', v)}>
                          <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                          <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>ZIP</Label>
                        <Input value={addr.zip_code} onChange={e => updateArrayItem('previous_addresses', i, 'zip_code', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>From Date</Label>
                      <Input type="date" value={addr.from_date} onChange={e => updateArrayItem('previous_addresses', i, 'from_date', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>To Date</Label>
                      <Input type="date" value={addr.to_date} onChange={e => updateArrayItem('previous_addresses', i, 'to_date', e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={() => addArrayItem('previous_addresses', emptyAddress)}>
                <Plus className="h-4 w-4 mr-1" /> Add Previous Address
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: License Information */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-blue-600" /> License Information</CardTitle>
              <CardDescription>CDL and medical card details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>License Number *</Label>
                  <Input value={form.license_number} onChange={e => updateForm('license_number', e.target.value)} className={fieldError('license_number')} data-testid="license-number-input" />
                  <ErrorMsg field="license_number" />
                </div>
                <div className="space-y-1.5">
                  <Label>Issuing State</Label>
                  <Select value={form.license_state} onValueChange={v => updateForm('license_state', v)}>
                    <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>License Class</Label>
                  <Select value={form.license_class} onValueChange={v => updateForm('license_class', v)}>
                    <SelectTrigger><SelectValue placeholder="Class" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Class A</SelectItem>
                      <SelectItem value="B">Class B</SelectItem>
                      <SelectItem value="C">Class C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>License Expiration</Label>
                  <Input type="date" value={form.license_expiration} onChange={e => updateForm('license_expiration', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Endorsements</Label>
                  <Input value={form.endorsements} onChange={e => updateForm('endorsements', e.target.value)} placeholder="e.g. H, N, T, P, X" />
                </div>
                <div className="space-y-1.5">
                  <Label>Restrictions</Label>
                  <Input value={form.restrictions} onChange={e => updateForm('restrictions', e.target.value)} placeholder="e.g. L, Z, E" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Medical Card Expiration</Label>
                  <Input type="date" value={form.medical_card_expiration} onChange={e => updateForm('medical_card_expiration', e.target.value)} />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="denied" checked={form.has_license_been_denied} onCheckedChange={v => updateForm('has_license_been_denied', v)} />
                  <Label htmlFor="denied" className="cursor-pointer text-sm">Has your license ever been denied, revoked, or cancelled?</Label>
                </div>
                {form.has_license_been_denied && (
                  <Textarea value={form.denial_details} onChange={e => updateForm('denial_details', e.target.value)} placeholder="Provide details..." rows={2} />
                )}
                <div className="flex items-center gap-2">
                  <Checkbox id="suspended" checked={form.has_license_been_suspended} onCheckedChange={v => updateForm('has_license_been_suspended', v)} />
                  <Label htmlFor="suspended" className="cursor-pointer text-sm">Has your license ever been suspended?</Label>
                </div>
                {form.has_license_been_suspended && (
                  <Textarea value={form.suspension_details} onChange={e => updateForm('suspension_details', e.target.value)} placeholder="Provide details..." rows={2} />
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Driving Experience */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-blue-600" /> Driving Experience</CardTitle>
              <CardDescription>Select all equipment types you have experience operating</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Total Years of CMV Experience *</Label>
                <Input value={form.years_experience} onChange={e => updateForm('years_experience', e.target.value)} placeholder="e.g. 5" className={`max-w-[200px] ${fieldError('years_experience')}`} />
                <ErrorMsg field="years_experience" />
              </div>
              <div>
                <Label className="mb-3 block">Equipment Experience (select all that apply)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {EQUIPMENT_TYPES.map(type => (
                    <button
                      key={type}
                      onClick={() => toggleEquipment(type)}
                      className={`p-2.5 sm:p-3 rounded-lg border text-sm text-left transition-colors ${
                        form.equipment_experience.includes(type)
                          ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {form.equipment_experience.includes(type) && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                        <span className="text-xs sm:text-sm">{type}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Accidents & Violations */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-blue-600" /> Accidents & Traffic Violations</CardTitle>
              <CardDescription>List all accidents and violations from the past 3 years</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm">Accidents (Past 3 Years)</h4>
                  <Button variant="outline" size="sm" onClick={() => addArrayItem('accidents', emptyAccident)}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                {form.accidents.length === 0 && <p className="text-sm text-slate-500 italic">No accidents reported.</p>}
                {form.accidents.map((acc, i) => (
                  <div key={i} className="border rounded-lg p-3 sm:p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Accident #{i + 1}</span>
                      <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeArrayItem('accidents', i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Date</Label>
                        <Input type="date" value={acc.date} onChange={e => updateArrayItem('accidents', i, 'date', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Nature of Accident</Label>
                        <Input value={acc.nature} onChange={e => updateArrayItem('accidents', i, 'nature', e.target.value)} placeholder="e.g. Rear-end collision" />
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={acc.fatalities} onCheckedChange={v => updateArrayItem('accidents', i, 'fatalities', v)} />
                          <Label className="text-sm">Fatalities</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox checked={acc.injuries} onCheckedChange={v => updateArrayItem('accidents', i, 'injuries', v)} />
                          <Label className="text-sm">Injuries</Label>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Description</Label>
                        <Input value={acc.description} onChange={e => updateArrayItem('accidents', i, 'description', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm">Traffic Violations (Past 3 Years)</h4>
                  <Button variant="outline" size="sm" onClick={() => addArrayItem('violations', emptyViolation)}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                {form.violations.length === 0 && <p className="text-sm text-slate-500 italic">No violations reported.</p>}
                {form.violations.map((viol, i) => (
                  <div key={i} className="border rounded-lg p-3 sm:p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Violation #{i + 1}</span>
                      <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeArrayItem('violations', i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Date</Label>
                        <Input type="date" value={viol.date} onChange={e => updateArrayItem('violations', i, 'date', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Location (City, State)</Label>
                        <Input value={viol.location} onChange={e => updateArrayItem('violations', i, 'location', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Charge</Label>
                        <Input value={viol.charge} onChange={e => updateArrayItem('violations', i, 'charge', e.target.value)} placeholder="e.g. Speeding" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Penalty</Label>
                        <Input value={viol.penalty} onChange={e => updateArrayItem('violations', i, 'penalty', e.target.value)} placeholder="e.g. Fine $200" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 6: Employment History */}
        {step === 6 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Briefcase className="h-5 w-5 text-blue-600" /> Employment History</CardTitle>
              <CardDescription>List all employers for the past 10 years (FMCSA requirement). No gaps &gt; 30 days.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {validationErrors['employment_0_company'] && (
                <p className="text-xs text-red-500 bg-red-50 p-2 rounded">{validationErrors['employment_0_company']}</p>
              )}
              {form.employment_history.map((emp, i) => (
                <div key={i} className="border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold">Employer #{i + 1}</span>
                    {form.employment_history.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeArrayItem('employment_history', i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label>Company Name {i === 0 ? '*' : ''}</Label>
                      <Input value={emp.company_name} onChange={e => updateArrayItem('employment_history', i, 'company_name', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Address</Label>
                      <Input value={emp.address} onChange={e => updateArrayItem('employment_history', i, 'address', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1.5">
                        <Label>City</Label>
                        <Input value={emp.city} onChange={e => updateArrayItem('employment_history', i, 'city', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>State</Label>
                        <Input value={emp.state} onChange={e => updateArrayItem('employment_history', i, 'state', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>ZIP</Label>
                        <Input value={emp.zip_code} onChange={e => updateArrayItem('employment_history', i, 'zip_code', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Phone</Label>
                      <Input value={emp.phone} onChange={e => updateArrayItem('employment_history', i, 'phone', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Position Held</Label>
                      <Input value={emp.position} onChange={e => updateArrayItem('employment_history', i, 'position', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>From Date</Label>
                      <Input type="date" value={emp.from_date} onChange={e => updateArrayItem('employment_history', i, 'from_date', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>To Date</Label>
                      <Input type="date" value={emp.to_date} onChange={e => updateArrayItem('employment_history', i, 'to_date', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Reason for Leaving</Label>
                      <Input value={emp.reason_for_leaving} onChange={e => updateArrayItem('employment_history', i, 'reason_for_leaving', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Supervisor Name</Label>
                      <Input value={emp.supervisor_name} onChange={e => updateArrayItem('employment_history', i, 'supervisor_name', e.target.value)} />
                    </div>
                    <div className="sm:col-span-2 flex flex-wrap items-center gap-4 sm:gap-6">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={emp.dot_regulated} onCheckedChange={v => updateArrayItem('employment_history', i, 'dot_regulated', v)} />
                        <Label className="text-xs sm:text-sm">Subject to DOT/FMCSA Regulations</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={emp.safety_sensitive} onCheckedChange={v => updateArrayItem('employment_history', i, 'safety_sensitive', v)} />
                        <Label className="text-xs sm:text-sm">Safety-Sensitive Function (49 CFR Part 40)</Label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={() => addArrayItem('employment_history', emptyEmployment)}>
                <Plus className="h-4 w-4 mr-1" /> Add Another Employer
              </Button>

              <div className="border-t pt-4 space-y-1.5">
                <Label>Explain any gaps in employment greater than 30 days</Label>
                <Textarea value={form.unemployment_periods} onChange={e => updateForm('unemployment_periods', e.target.value)} rows={3} placeholder="Describe any periods of unemployment..." />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 7: Education & References */}
        {step === 7 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-blue-600" /> Education & References</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-sm mb-3">Education</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Highest Level of Education</Label>
                    <Select value={form.highest_education} onValueChange={v => updateForm('highest_education', v)}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high_school">High School / GED</SelectItem>
                        <SelectItem value="some_college">Some College</SelectItem>
                        <SelectItem value="associates">Associate's Degree</SelectItem>
                        <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                        <SelectItem value="masters">Master's Degree</SelectItem>
                        <SelectItem value="cdl_school">CDL School / Trade School</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>School / Institution Name</Label>
                    <Input value={form.school_name} onChange={e => updateForm('school_name', e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm">Professional References</h4>
                </div>
                {form.references.map((ref, i) => (
                  <div key={i} className="border rounded-lg p-3 sm:p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Reference #{i + 1}</span>
                      {form.references.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeArrayItem('references', i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Full Name</Label>
                        <Input value={ref.name} onChange={e => updateArrayItem('references', i, 'name', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Phone Number</Label>
                        <Input value={ref.phone} onChange={e => updateArrayItem('references', i, 'phone', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Relationship</Label>
                        <Input value={ref.relationship} onChange={e => updateArrayItem('references', i, 'relationship', e.target.value)} placeholder="e.g. Former Supervisor" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Years Known</Label>
                        <Input value={ref.years_known} onChange={e => updateArrayItem('references', i, 'years_known', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addArrayItem('references', emptyReference)}>
                  <Plus className="h-4 w-4 mr-1" /> Add Reference
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 8: MVR Authorization & Signature */}
        {step === 8 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-600" /> MVR Authorization & Signature</CardTitle>
              <CardDescription>Federal Motor Carrier Safety Regulations require authorization to obtain your driving record</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 dark:bg-slate-900 border rounded-lg p-3 sm:p-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed space-y-3 max-h-64 overflow-y-auto">
                <p className="font-bold text-slate-900 dark:text-slate-100">AUTHORIZATION FOR RELEASE OF MOTOR VEHICLE RECORD (MVR)</p>
                <p>
                  I hereby authorize the release of my Motor Vehicle Record (MVR) to the prospective employer 
                  and its authorized representatives. I understand that my MVR will be used for employment 
                  consideration and/or ongoing employment compliance with Federal Motor Carrier Safety 
                  Regulations (FMCSRs) under 49 CFR Parts 382, 383, and 391.
                </p>
                <p>
                  I understand that pursuant to Section 604(b)(2)(A) of the Fair Credit Reporting Act, 
                  the employer may obtain my MVR at any time during my employment without additional 
                  authorization from me, so long as I am employed by the company.
                </p>
                <p>
                  I certify that the information provided in this application is true and complete to the 
                  best of my knowledge. I understand that any falsification, misrepresentation, or omission 
                  of facts may be cause for denial of employment or immediate termination of employment 
                  regardless of when discovered.
                </p>
                <p>
                  I authorize the company to investigate my background, including but not limited to: 
                  driving record (MVR), employment verification, criminal history, and drug/alcohol testing 
                  history through the FMCSA Drug and Alcohol Clearinghouse, as required by 49 CFR Part 382 
                  Subpart G.
                </p>
                <p className="font-semibold text-slate-900">
                  I HAVE READ AND UNDERSTAND THE ABOVE AUTHORIZATION AND VOLUNTARILY CONSENT TO THE 
                  RELEASE OF MY MOTOR VEHICLE RECORD AND BACKGROUND INFORMATION.
                </p>
              </div>

              <div className="space-y-4 border rounded-lg p-3 sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Printed Name (Digital Signature) *</Label>
                    <Input 
                      value={form.mvr_signature_name} 
                      onChange={e => updateForm('mvr_signature_name', e.target.value)} 
                      placeholder="Type your full legal name"
                      className={`font-serif italic text-lg ${fieldError('mvr_signature_name')}`}
                      data-testid="mvr-signature-input"
                    />
                    <ErrorMsg field="mvr_signature_name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date *</Label>
                    <Input type="date" value={form.mvr_signature_date} onChange={e => updateForm('mvr_signature_date', e.target.value)} className={fieldError('mvr_signature_date')} data-testid="mvr-date-input" />
                    <ErrorMsg field="mvr_signature_date" />
                  </div>
                </div>

                <div className={`flex items-start gap-2 p-3 rounded-lg border ${validationErrors.mvr_consent ? 'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800' : 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'}`}>
                  <Checkbox 
                    id="mvr_consent" 
                    checked={form.mvr_consent} 
                    onCheckedChange={v => updateForm('mvr_consent', v)}
                    data-testid="mvr-consent-checkbox"
                  />
                  <Label htmlFor="mvr_consent" className="cursor-pointer text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                    I authorize the release of my Motor Vehicle Record (MVR) and consent to background 
                    investigation as described above.
                  </Label>
                </div>
                <ErrorMsg field="mvr_consent" />

                <div className={`flex items-start gap-2 p-3 rounded-lg border ${validationErrors.certify_information_true ? 'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800' : 'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800'}`}>
                  <Checkbox 
                    id="certify" 
                    checked={form.certify_information_true} 
                    onCheckedChange={v => updateForm('certify_information_true', v)}
                    data-testid="certify-checkbox"
                  />
                  <Label htmlFor="certify" className="cursor-pointer text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                    I certify that all information provided in this application is true and complete. 
                    I understand that falsification or omission may result in denial of employment 
                    or termination.
                  </Label>
                </div>
                <ErrorMsg field="certify_information_true" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 9: FMCSA Drug & Alcohol Clearinghouse Consent */}
        {step === 9 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-blue-600" /> FMCSA Drug & Alcohol Clearinghouse</CardTitle>
              <CardDescription>Consent for Queries and Release of Information (49 CFR Part 382, Subpart G)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 dark:bg-slate-900 border rounded-lg p-3 sm:p-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed space-y-3 max-h-64 overflow-y-auto">
                <p className="font-bold text-slate-900 dark:text-slate-100">FMCSA DRUG AND ALCOHOL CLEARINGHOUSE CONSENT</p>
                <p>
                  In accordance with 49 CFR Part 40 and 49 CFR Part 382, Subpart G, the Federal Motor 
                  Carrier Safety Administration (FMCSA) requires employers to query the FMCSA Drug and 
                  Alcohol Clearinghouse prior to hiring a driver to perform safety-sensitive functions, 
                  and annually thereafter.
                </p>
                <p>
                  The Clearinghouse is a secure online database that gives employers, the FMCSA, State 
                  Driver Licensing Agencies (SDLAs), and State law enforcement personnel real-time information 
                  about commercial driver's license (CDL) and commercial learner's permit (CLP) holders' 
                  drug and alcohol program violations.
                </p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">CONSENT:</p>
                <p>
                  I hereby provide my consent to the prospective/current employer to conduct a <strong>full query</strong> of 
                  the FMCSA Drug and Alcohol Clearinghouse to determine whether drug or alcohol violation 
                  information about me exists in the Clearinghouse.
                </p>
                <p>
                  I understand that if the Clearinghouse query reveals that drug or alcohol violation 
                  information about me exists in the Clearinghouse, FMCSA will not disclose that information 
                  to the employer without first sending me an electronic consent request.
                </p>
                <p>
                  I further understand that if I refuse to provide consent for the employer to conduct a 
                  full query, the employer must not permit me to perform any safety-sensitive function, 
                  including driving a commercial motor vehicle (CMV), as required by 49 CFR 382.702(b).
                </p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">DURATION:</p>
                <p>
                  This consent is valid for the duration of my employment with the above-named employer, 
                  or until I revoke it in writing, whichever occurs first. I understand that I may revoke 
                  this consent at any time by notifying the employer in writing.
                </p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">ACKNOWLEDGMENT:</p>
                <p>
                  I have read and understand this consent form. I voluntarily agree to the release of my 
                  Drug and Alcohol Clearinghouse records as described above. I understand my rights under 
                  49 CFR Part 382, Subpart G.
                </p>
              </div>

              <div className="space-y-4 border rounded-lg p-3 sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Printed Name (Digital Signature) *</Label>
                    <Input 
                      value={form.fmcsa_signature_name} 
                      onChange={e => updateForm('fmcsa_signature_name', e.target.value)} 
                      placeholder="Type your full legal name"
                      className={`font-serif italic text-lg ${fieldError('fmcsa_signature_name')}`}
                      data-testid="fmcsa-signature-input"
                    />
                    <ErrorMsg field="fmcsa_signature_name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date *</Label>
                    <Input type="date" value={form.fmcsa_signature_date} onChange={e => updateForm('fmcsa_signature_date', e.target.value)} className={fieldError('fmcsa_signature_date')} data-testid="fmcsa-date-input" />
                    <ErrorMsg field="fmcsa_signature_date" />
                  </div>
                </div>

                <div className={`flex items-start gap-2 p-3 rounded-lg border ${validationErrors.fmcsa_consent ? 'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800' : 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'}`}>
                  <Checkbox 
                    id="fmcsa_consent" 
                    checked={form.fmcsa_consent} 
                    onCheckedChange={v => updateForm('fmcsa_consent', v)}
                    data-testid="fmcsa-consent-checkbox"
                  />
                  <Label htmlFor="fmcsa_consent" className="cursor-pointer text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                    I consent to a full query of the FMCSA Drug and Alcohol Clearinghouse as described above 
                    and authorize the release of my Clearinghouse records to the prospective employer.
                  </Label>
                </div>
                <ErrorMsg field="fmcsa_consent" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 10: Fair Credit Reporting Act Authorization */}
        {step === 10 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-blue-600" /> Fair Credit Reporting Authorization</CardTitle>
              <CardDescription>Disclosure and Authorization for Background Investigation under FCRA</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 dark:bg-slate-900 border rounded-lg p-3 sm:p-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed space-y-3 max-h-64 overflow-y-auto">
                <p className="font-bold text-slate-900 dark:text-slate-100">FAIR CREDIT REPORTING ACT (FCRA) - DISCLOSURE AND AUTHORIZATION</p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">DISCLOSURE:</p>
                <p>
                  In accordance with the Fair Credit Reporting Act ("FCRA"), 15 U.S.C. &sect; 1681 et seq., 
                  this is to inform you that a consumer report and/or investigative consumer report may be 
                  obtained for employment purposes as part of the pre-employment background investigation 
                  and at any time during your employment.
                </p>
                <p>
                  The report may contain information regarding your character, general reputation, personal 
                  characteristics, mode of living, work habits, performance, experience, along with reasons 
                  for termination of past employment, and your driving record, including any traffic citations.
                </p>
                <p>
                  The report may also include information from public records, including but not limited to: 
                  criminal history records from any criminal justice agency in any or all federal, state, 
                  and county jurisdictions; driving records; civil court records; credit reports; education 
                  records; and any other public records.
                </p>
                <p className="font-semibold text-slate-900 dark:text-slate-100">YOUR RIGHTS UNDER THE FCRA:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>You have the right to request disclosure of the nature and scope of any investigative consumer report.</li>
                  <li>You have the right to request a written summary of your rights under the FCRA.</li>
                  <li>Before any adverse action is taken based on information in a consumer report, you will be provided a copy of the report and a description of your rights.</li>
                  <li>You have the right to dispute the accuracy or completeness of any information in your consumer report by contacting the consumer reporting agency directly.</li>
                </ul>
                <p className="font-semibold text-slate-900 dark:text-slate-100">AUTHORIZATION:</p>
                <p>
                  I have carefully read and understand this Disclosure and Authorization. By signing below, 
                  I voluntarily and knowingly authorize the prospective employer to obtain consumer reports 
                  and/or investigative consumer reports about me from one or more consumer reporting agencies. 
                  I also authorize all entities having information about me to release such information to 
                  the employer or its designated agent.
                </p>
                <p>
                  I release the employer and all persons, agencies, and entities providing information or 
                  reports about me from any and all liability arising out of the requests for, or release of, 
                  any of the above-mentioned information or reports.
                </p>
                <p>
                  This authorization shall remain in effect over the course of my employment, to the extent 
                  permitted by law.
                </p>
              </div>

              <div className="space-y-4 border rounded-lg p-3 sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Printed Name (Digital Signature) *</Label>
                    <Input 
                      value={form.fcra_signature_name} 
                      onChange={e => updateForm('fcra_signature_name', e.target.value)} 
                      placeholder="Type your full legal name"
                      className={`font-serif italic text-lg ${fieldError('fcra_signature_name')}`}
                      data-testid="fcra-signature-input"
                    />
                    <ErrorMsg field="fcra_signature_name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date *</Label>
                    <Input type="date" value={form.fcra_signature_date} onChange={e => updateForm('fcra_signature_date', e.target.value)} className={fieldError('fcra_signature_date')} data-testid="fcra-date-input" />
                    <ErrorMsg field="fcra_signature_date" />
                  </div>
                </div>

                <div className={`flex items-start gap-2 p-3 rounded-lg border ${validationErrors.fcra_consent ? 'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800' : 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'}`}>
                  <Checkbox 
                    id="fcra_consent" 
                    checked={form.fcra_consent} 
                    onCheckedChange={v => updateForm('fcra_consent', v)}
                    data-testid="fcra-consent-checkbox"
                  />
                  <Label htmlFor="fcra_consent" className="cursor-pointer text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                    I authorize the employer to obtain consumer reports and investigative consumer reports 
                    about me for employment purposes as described above. I release all parties from liability 
                    in connection with such reports.
                  </Label>
                </div>
                <ErrorMsg field="fcra_consent" />
              </div>

              {/* Submit Button on final step */}
              <Button 
                className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700"
                onClick={handleSubmit}
                disabled={submitting || !form.fcra_consent}
                data-testid="submit-application-btn"
              >
                {submitting ? 'Submitting Application...' : 'Submit Application'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Navigation Footer - mobile-safe with proper spacing */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-20">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between safe-area-bottom">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setStep(Math.max(1, step - 1))} 
            disabled={step === 1}
            className="h-9 px-3 sm:px-4"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-0.5 sm:mr-1" /> <span className="hidden sm:inline">Previous</span><span className="sm:hidden">Back</span>
          </Button>
          <span className="text-xs sm:text-sm text-muted-foreground font-medium">{step} / {STEPS.length}</span>
          {step < STEPS.length ? (
            <Button onClick={nextStep} size="sm" className="h-9 px-3 sm:px-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="next-step-btn">
              Next <ChevronRight className="h-4 w-4 ml-0.5 sm:ml-1" />
            </Button>
          ) : (
            <Button 
              className="bg-green-600 hover:bg-green-700 text-white h-9 px-3 sm:px-4"
              size="sm"
              onClick={handleSubmit}
              disabled={submitting || !form.fcra_consent}
            >
              <Check className="h-4 w-4 mr-0.5 sm:mr-1" /> Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
